<template>
  <div class="card border-0 shadow-sm">
    <div class="card-header bg-white pt-3 pb-3 border-bottom">
      <div class="row align-items-center g-3">
        <div class="col-md-7">
          <div class="d-flex align-items-center gap-2">
            <h2 class="fs-5 fw-bold mb-0 text-dark">{{ title }}</h2>
            <span class="text-muted fs-6">
              ({{ visitType }} &bull; {{ processedParticipants.length }})
            </span>
          </div>
        </div>

        <div class="col-md-5">
          <div class="input-group input-group-sm">
            <span class="input-group-text bg-light"><i class="bi bi-search"></i></span>
            <input
              v-model="searchQuery"
              type="text"
              class="form-control"
              placeholder="Filter by name or STAR ID..."
            />
            <button 
              v-if="searchQuery" 
              class="btn btn-outline-secondary" 
              type="button" 
              @click="searchQuery = ''"
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="card-body p-0">
      <div 
        v-if="processedParticipants.length > 0" 
        class="table-responsive border-start border-3"
        :class="visitType === 'Core' ? 'border-visit-core' : 'border-visit-short'"
        style="max-height: 600px;"
      >
        <table class="table table-hover align-middle mb-0">
          <thead class="table-light sticky-top">
            <tr>
              <slot 
                name="columns" 
                :toggle-sort="toggleSort" 
                :sort-key="sortKey" 
                :sort-dir="sortDir"
              />
            </tr>
          </thead>
          <tbody>
            <slot name="rows" :participants="processedParticipants" />
          </tbody>
        </table>
      </div>

      <div v-else class="text-center py-5 text-muted">
        <i class="bi bi-inbox fs-1 d-block mb-2"></i>
        <p class="mb-0 fs-6">No {{ visitType.toLowerCase() }} participants found.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
  participants: {
    type: Array,
    default: () => []
  },
  title: {
    type: String,
    required: true
  },
  visitType: {
    type: String,
    default: 'Core'
  }
});

const searchQuery = ref('');
const sortKey = ref('star_id'); // Default sort field
const sortDir = ref('asc');     // 'asc' or 'desc'

const themeColor = computed(() => {
  return props.visitType === 'Core' ? '#008080' : '#8A2BE2';
});

const filteredParticipants = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return props.participants;

  return props.participants.filter((p) => {
    const name = `${p.first_name ?? ''} ${p.last_name ?? ''}`.toLowerCase();
    const id = String(p.id ?? '').toLowerCase();
    const starId = String(p.star_id ?? '').toLowerCase();
    return name.includes(query) || id.includes(query) || starId.includes(query);
  });
});

const processedParticipants = computed(() => {
  const list = [...filteredParticipants.value];
  if (!sortKey.value) return list;

  return list.sort((a, b) => {
    let valA = getFieldValue(a, sortKey.value);
    let valB = getFieldValue(b, sortKey.value);

    // Normalize strings for case-insensitive comparison
    if (typeof valA === 'string') valA = valA.toLowerCase();
    if (typeof valB === 'string') valB = valB.toLowerCase();

    // Handle null/undefined values
    if (valA == null) return 1;
    if (valB == null) return -1;

    if (valA < valB) return sortDir.value === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir.value === 'asc' ? 1 : -1;
    return 0;
  });
});

// Extract value nested in object based on key name
const getFieldValue = (item, key) => {
  switch (key) {
    case 'name':
      return `${item.first_name ?? ''} ${item.last_name ?? ''}`;
    case 'targetMonth':
      return item.targetMonth;
    case 'date':
      return item.tasks?.[0]?.appointments?.[0]?.starts_at ?? '';
    case 'star_id':
    default:
      return item.star_id;
  }
};

const toggleSort = (key) => {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortDir.value = 'asc';
  }
};
</script>